import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { getUnixTimestamp, getUUID } from 'helpers/utils';
import { errorToast } from 'configuration/notifications';
import { cashWarrantApi } from 'helpers/api/cashWarrant';

import { CashWarrant } from 'store/cashWarrant/actions';
import { rentSelector } from 'store/transferAct/selectors';
import { cashBoxListSelector, cashWarrantStatus } from 'store/cashWarrant/selectors';
import { stockSelector } from 'store/stock/selectors';
import { userDataSelector } from 'store/auth/selectors';
import { useFirstRender } from 'hooks/useFirstRender';
import { successToast } from 'configuration/notifications';
import { ButtonIcon } from 'components/Button';
import { ModalAttention } from 'components/ModalAttention';
import { Header } from 'components/Header';

import { CashWarrantForm } from 'components/CashWarrantForm';
import { CashWarrantCard } from 'components/CashWarrantCard';

import './style.scss';

const CheckoutRentPage = () => {
  const { rentId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const firstRender = useFirstRender();
  const rent = useSelector(rentSelector);
  const cashBoxes = useSelector(cashBoxListSelector);
  const stock = useSelector(stockSelector);
  const userData = useSelector(userDataSelector);
  const warrantStatus = useSelector(cashWarrantStatus);

  const [copiedCashWarrantID, setCopiedCashWarrantID] = useState(null);
  const [attentionModal, setAttentionModal] = useState(null);
  const [pending, setPending] = useState(false);
  const [isPledge, setIsPledge] = useState(false);
  const [cashWarrants, setCashWarrants] = useState([]);
  const [editingCashWarrantId, setEditingCashWarrantId] = useState(undefined);

  useEffect(() => {
    if (!firstRender) {
      return;
    }
    if (rent) {
      dispatch(CashWarrant.getCashBoxes());
      return;
    }
    history.push('/');
    errorToast('Необходимые для оформления поля не найдены!');
  }, []);

  useEffect(() => {
    if (firstRender) {
      return;
    }
    if (rent && cashBoxes) {
      if (rentId) {
        getCashWarrants(rentId);
        return;
      }
      createCashWarrant();
      return;
    }
  }, [cashBoxes]);

  useEffect(() => {
    if (warrantStatus !== 0) {
      dispatch(CashWarrant.setCashWarrantStatus(0));
      history.push(copiedCashWarrantID ? `/rent/${rent?.actID1C}/checkout` : '/');
      const init = async () => {
        if (copiedCashWarrantID) {
          const { data } = await cashWarrantApi.getCashWarrantUrl(copiedCashWarrantID);
          const url = data.url;
          copyUrl(url);
          updateCashWarrantByID1C(copiedCashWarrantID, 'url', url);
          setCopiedCashWarrantID(null);
        }
      };
      init();
    }
  }, [warrantStatus, dispatch, history, rent, copiedCashWarrantID]);

  const rentSum = useMemo(() => {
    let sum = 0;

    if (rent) {
      const rateVAT = rent?.agr?.organization?.rateVAT || 0;

      const instSum =
        rent &&
        rent.instruments.reduce((acc, cur) => {
          let additionalSum = 0;

          if (!isPledge) {
            additionalSum += Number(cur.pledge) * cur.quantity;
          }
          return (
            acc + (additionalSum + cur.itemRentSum * cur.quantity + ((cur.itemRentSum * cur.quantity) / 100) * rateVAT)
          );
        }, 0);
      sum += Number(instSum);

      if (rent.bonusAccured) {
        sum -= Number(rent.bonusSpent);
      }

      sum += rent.fine.reduce((acc, n) => acc + Number(n.sum), 0);
      sum -= rent.discount.reduce((acc, n) => acc + Number(n.sum), 0);
    }
    return sum;
  }, [rent, isPledge]);

  const rentSumTotal = useMemo(() => {
    return rentSum - cashWarrants.reduce((acc, cur) => acc + cur.sum, 0);
  }, [rentSum, cashWarrants]);

  useEffect(() => {
    if (cashWarrants.length === 1) {
      setCashWarrants([{ ...cashWarrants[0], sum: rentSum }]);
    }
  }, [rentSum]);

  const mapWarrants = (warrants) =>
    warrants.map((warrant) => ({
      id: warrant.id,
      warrantID1C: warrant.warrantID1C,
      sum: warrant.sum,
      type: warrant.type,
      isPaidOff: warrant.isPaidOff,
      url: warrant.url,
      selected: warrant.cashboxID,
      comment: warrant.comment,
      isGettingBy1C: warrant.isGettingBy1C,
      conduct: warrant.conduct,
      nonConduct: warrant.nonConduct,
      pdfBill: warrant.pdfBill,
      isReadyFor1C: warrant.isReadyFor1C,
      createdNow: false,
    }));

  const getCashWarrants = (id) => {
    cashWarrantApi.getCashWarrants(id).then(({ data } = {}) => {
      const newCashWarrants = mapWarrants(data.results);
      if (newCashWarrants.length > 0) {
        setCashWarrants(newCashWarrants);
        setIsPledge(data.results[0].isPledge);
        return;
      }

      createCashWarrant();
    });
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    successToast('Ссылка скопирована в буфер обмена');
  };

  const handleUrlClick = async (currentWarrant) => {
    let url = currentWarrant.url;
    if (!url) {
      if (!rent?.actID1C) {
        setCopiedCashWarrantID(currentWarrant.warrantID1C);
        await handleSave({ isFor1C: false, isSaveRent: true });
        return;
      }
      const { data } = await cashWarrantApi.getCashWarrantUrl(currentWarrant.warrantID1C);
      url = data.url;
    }
    copyUrl(url);
  };

  const handleClose = (isSubmit) => {
    if (!isSubmit) {
      setAttentionModal(true);
      return;
    }

    toggleAttentionModal(false);
    history.push('/');
  };

  const toggleAttentionModal = (toggle) => {
    setAttentionModal(toggle);
  };

  const togglePledge = () => {
    setIsPledge(!isPledge);
  };

  const toggleUnofficialWarrant = (id) => {
    const findIndex = cashWarrants.findIndex((item) => item.id === id);

    /// Меняем значение ордера по ключу
    if (findIndex !== -1) {
      const newWarrants = cashWarrants.slice();
      newWarrants[findIndex].nonConduct = !cashWarrants[findIndex].nonConduct;
      newWarrants[findIndex].sum = 0;
      setCashWarrants(newWarrants);
    }
  };

  const handleBack = async () => {
    const backUrl = rent?.actID1C ? `/rent/${rent?.actID1C}` : '/rent';
    history.push(backUrl, { fromCheckout: true });
  };

  const handleSave = async ({ isFor1C, isSaveRent, warrantID1CPendingPayment }) => {
    const cashWarrantsSum = cashWarrants.reduce((acc, cur) => acc + cur.sum, 0);

    if (cashWarrants.length === 0) {
      return errorToast('Нужно добавить хотя бы один кассовый ордер!');
    }

    if (cashWarrants.every((w) => !w.nonConduct) && rentSum !== cashWarrantsSum) {
      return errorToast(`Сумма в кассовых ордерах должна быть равна сумме в аренде (${rentSum} != ${cashWarrantsSum})`);
    }

    for (const warrant of cashWarrants) {
      if (warrant.sum <= 0) {
        return errorToast('Сумма в кассовых ордерах должна быть больше нуля');
      }

      if (warrant.comment.length === 0 && isPledge) {
        return errorToast('В случае принятия залога необходимо указывать комментарий!');
      }

      if (warrant.comment.length === 0 && warrant.nonConduct) {
        return errorToast('Комментарий обязателен для неофициального приходника!');
      }

      if (!warrant.selected) {
        return errorToast('Нужно выбрать кассу!');
      }
    }

    if (pending) {
      return errorToast('Ордер уже сохраняется!');
    }

    const accruals = [
      ...rent.fine.map((elem) => ({
        accrualID1C: elem?.id,
        sum: elem.sum,
        type: 'Штраф',
        comment: elem.comment,
        accrualSubTypeID: elem.accrualSubType.id,
      })),
      ...rent.discount.map((elem) => ({
        accrualID1C: elem?.id,
        sum: elem.sum,
        type: 'Скидка',
        comment: elem.comment,
        accrualSubTypeID: elem.accrualSubType.id,
      })),
    ];

    const warrantsData = cashWarrants.map((warrant) => ({
      sum: warrant.sum,
      warrantID1C: warrant.warrantID1C,
      type: warrant.type,
      comment: warrant.comment,
      cashboxID1C: warrant.selected.cashboxID1C,
      actID: rentId || null,
      isPledge,
      url: warrant.url,
      isPeningPayment: warrantID1CPendingPayment === warrant.warrantID1C,
      isGettingBy1C: warrant.isGettingBy1C || false,
      conduct: warrant.conduct || false,
      nonConduct: warrant.nonConduct || false,
      pdfBill: warrant.pdfBill || '',
      isReadyFor1C: warrant.isReadyFor1C || false,
    }));

    setPending(true);

    let status = 'Ожидает обработки';
    if (!isSaveRent && stock?.isSingleWorker) {
      status = 'В аренде';
    } else if (!isSaveRent && isFor1C) {
      status = 'Ожидает выдачи инструмента';
    }

    if (rentId) {
      const rentData = {
        agreementID: rent.agr.id,
        stockID: stock.stockID1C,
        status,
        AddrExp: rent.address,
        clientID: rent.client.id,
        bonusSpent: rent.bonusSpent || 0,
        bonusAccured: 0,
        endDateTime: rent.endDateTime,
        comment: rent.comment,
        chosenItems: rent.instruments,
        accruals,
      };

      if (isFor1C) {
        rentData.isReadyFor1C = true;
        rentData.conduct = false;
      }
      await dispatch(CashWarrant.updateTransferActBoxes(rentId, rentData, warrantsData));
    } else {
      const rentData = {
        clientID: rent.client.id,
        stockID: stock.stockID1C,
        AddrExp: rent.address,
        bonusSpent: rent.bonusSpent || 0,
        bonusAccured: 0,
        type: 'give',
        isApproved: 3,
        status,
        startDateTime: getUnixTimestamp(),
        endDateTime: rent.endDateTime,
        comment: rent.comment,
        agreementID: rent.agr.id,
        chosenItems: rent.instruments,
        accruals,
      };

      if (isFor1C) {
        rentData.isReadyFor1C = true;
        rentData.conduct = false;
      }

      const warrantsAPIData = await dispatch(CashWarrant.createTransferActBoxes(rentData, warrantsData));
      setCashWarrants(mapWarrants(warrantsAPIData));
    }
    setPending(false);
  };

  const removeCashWarrant = (id) => {
    const findIndex = cashWarrants.findIndex((item) => item.id === id);

    if (findIndex !== -1) {
      const newWarrants = cashWarrants.slice();
      const oldSum = newWarrants[findIndex].sum >= 0 ? newWarrants[findIndex].sum : 0;
      const cashWarrantsSum = cashWarrants.reduce((acc, cur) => acc + cur.sum, 0);

      /// При удалении ордера нужно передавать сумму в соседний ордер
      /// Передается в предыдущий ордер, если это первый ордер, во второй

      if (rentSum >= cashWarrantsSum) {
        if (findIndex === 0) {
          newWarrants[findIndex].sum += oldSum;
        } else {
          newWarrants[findIndex - 1].sum += oldSum;
        }
      }

      newWarrants.splice(findIndex, 1);
      setCashWarrants(newWarrants);
    }
  };

  const updateCashWarrant = (id, key, value) => {
    const findIndex = cashWarrants.findIndex((item) => item.id === id);
    if (findIndex !== -1) {
      const newWarrants = cashWarrants.slice();
      newWarrants[findIndex][key] = value;
      setCashWarrants(newWarrants);
    }
  };

  const updateCashWarrantByID1C = (id, key, value) => {
    const findIndex = cashWarrants.findIndex((item) => item.warrantID1C === id);
    if (findIndex !== -1) {
      const newWarrants = cashWarrants.slice();
      newWarrants[findIndex][key] = value;
      setCashWarrants(newWarrants);
    }
  };

  const createCashWarrant = () => {
    const worker = userData?.cashbox || null;
    const findCashBox = worker && cashBoxes?.find((item) => item.cashboxID1C === worker.cashboxID1C);

    const isUnofficialWarrant = cashWarrants.some((w) => w.nonConduct);

    /// В новый ордер нужно добавлять остаток суммы аренды с учётом предыдущих ордеров
    const sum = rentSum - cashWarrants.reduce((acc, cur) => acc + cur.sum, 0);
    const newCashWarrant = {
      id: Date.now(),
      type: 'income',
      warrantID1C: getUUID(),
      sum: isUnofficialWarrant ? 0 : sum >= 0 ? sum : 0,
      selected: findCashBox || null,
      comment: '',
      createdNow: true,
    };

    setCashWarrants([...cashWarrants, newCashWarrant]);
    setEditingCashWarrantId(newCashWarrant.id);
  };

  const editCashWarrant = (id) => {
    setEditingCashWarrantId(id);
  };

  const saveCashWarrant = () => {
    setEditingCashWarrantId(undefined);
  };

  const getIsUnofficialWarrant = (id) => {
    const warrant = cashWarrants.find((item) => item.id === id);
    return warrant?.nonConduct;
  };

  return (
    <>
      <Header title="Кассовый ордер" onClose={handleClose} />

      <main className="warrants-list">
        <div className="warrants-cards">
          <div className="warrants-section">
            {!!editingCashWarrantId && (!rent || !rent.status || rent.status === 'Ожидает обработки') && (
              <ButtonIcon
                className="btn-main btn-unofficial-warrant"
                style={getIsUnofficialWarrant(editingCashWarrantId) ? { backgroundColor: '#FFF7D9' } : {}}
                onClick={() => toggleUnofficialWarrant(editingCashWarrantId)}
                icon="credit"
                text="Неоф. приходник"
              />
            )}

            {(!rent ||
              !rent.status ||
              rent.status === 'Ожидает обработки' ||
              rent.status === 'Ожидает выдачи инструмента') && (
              <ButtonIcon
                className="btn-main btn-pledge"
                style={isPledge ? { backgroundColor: '#E5FFC6' } : {}}
                onClick={togglePledge}
                icon="warrant-accepted"
                text="Залог принят"
              />
            )}
          </div>
          <div></div>
          <div className="warrants-section-list">
            <h3> Приходные кассовые ордера</h3>

            {cashWarrants
              .filter((elem) => elem.type === 'income')
              .map((item, i) => {
                if (editingCashWarrantId === item.id) {
                  return (
                    <CashWarrantForm
                      key={i}
                      cashWarrant={item}
                      number={i + 1}
                      updateCashWarrant={updateCashWarrant}
                      removeCashWarrant={removeCashWarrant}
                      cashBoxes={cashBoxes}
                      isOnlyOneCashWarrant={cashWarrants.length === 1}
                      saveCashWarrant={() => saveCashWarrant()}
                      isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    />
                  );
                }
                return (
                  <CashWarrantCard
                    key={i}
                    cashWarrant={item}
                    rentSum={rentSum}
                    editCashWarrant={() => editCashWarrant(item.id)}
                    deleteCashWarrant={() => removeCashWarrant(item.id)}
                    isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    onUrlClick={() => {
                      handleUrlClick(item);
                    }}
                  />
                );
              })}

            {rentSumTotal > 0 && (
              <div className="cash-warrant-group">
                <ButtonIcon className="btn-main" onClick={createCashWarrant} icon="plus-round" text="Добавить" />
              </div>
            )}
          </div>
          <div className="warrants-section-list">
            <h3> Расходные кассовые ордера</h3>

            {cashWarrants
              .filter((elem) => elem.type === 'cost')
              .map((item, i) => {
                if (editingCashWarrantId === item.id) {
                  return (
                    <CashWarrantForm
                      key={i}
                      cashWarrant={item}
                      number={i + 1}
                      updateCashWarrant={updateCashWarrant}
                      removeCashWarrant={removeCashWarrant}
                      cashBoxes={cashBoxes}
                      isOnlyOneCashWarrant={cashWarrants.length === 1}
                      saveCashWarrant={() => saveCashWarrant()}
                      isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                    />
                  );
                }
                return (
                  <CashWarrantCard
                    key={i}
                    cashWarrant={item}
                    rent={rent}
                    rentSum={rentSum}
                    editCashWarrant={() => editCashWarrant(item.id)}
                    deleteCashWarrant={() => removeCashWarrant(item.id)}
                    isUnofficialWarrant={getIsUnofficialWarrant(item.id)}
                  />
                );
              })}
          </div>
        </div>

        <div className="buttons-block-grid">
          <div style={{ display: 'flex' }}>
            <ButtonIcon
              className="btn-main"
              onClick={handleBack}
              icon="arrow-left"
              text="Вернуться к оформлению аренды"
            />
          </div>

          {(!rent ||
            !rent.status ||
            rent.status === 'Ожидает обработки' ||
            rent.status === 'Ожидает выдачи инструмента') && (
            <div className="flex-center">
              <ButtonIcon
                className="btn-main"
                onClick={() => handleSave({ isFor1C: false, isSaveRent: true })}
                icon="save"
                text="Сохранить"
              />

              <ButtonIcon
                className="btn-main btn-save"
                onClick={() => handleSave({ isFor1C: true })}
                icon={stock?.isSingleWorker ? 'driller' : 'warehouse'}
                text={stock?.isSingleWorker ? 'Передать в аренду' : 'Передать на склад'}
              />
            </div>
          )}

          <div></div>
        </div>
      </main>

      {attentionModal && (
        <ModalAttention onClose={() => toggleAttentionModal(false)} onSubmit={() => handleClose(true)} />
      )}
    </>
  );
};

export { CheckoutRentPage };
